import { api } from './api';
import { useSession } from './store';
import { membershipHomeUrl, rememberTenantSlug } from './config';
import type { Branding, ClientProfile, MemberRole } from './types';

interface AcceptResult {
  membership: { role: MemberRole; clientId?: string; status: string };
  client: ClientProfile;
  tenant: { tenantId: string; slug: string; businessName: string; branding: Branding };
}

/**
 * Redeem a client invitation and put the resulting membership into the
 * session.
 *
 * Two screens do this: the landing page for someone who is already signed in,
 * and the sign-up form for someone who has just made an account. It lives here
 * so they cannot drift, and because getting the membership into the session
 * before navigating is the part that is easy to leave out and hard to notice:
 * the route guard reads the session, and a portal that bounces straight back
 * to the storefront is indistinguishable from the invitation having failed.
 *
 * Returns where to send them. A caller on the business's own host gets a path
 * to navigate; a caller on the platform host, where no tenant resolves and
 * every guarded route would bounce, gets a full URL to assign instead.
 */
export async function redeemClientInvite(
  token: string,
  details: { fullName?: string; mobile?: string } = {},
): Promise<{ path: string; url: string | null }> {
  const result = await api<AcceptResult>('/clients/accept', {
    method: 'POST',
    body: { token, ...details },
  });

  const { tenant, membership } = result;
  rememberTenantSlug(tenant.slug);

  // The membership list comes from an eventually consistent index, so the
  // response to the write is what we trust rather than reading it back.
  useSession.getState().adoptMembership({
    tenantId: tenant.tenantId,
    slug: tenant.slug,
    businessName: tenant.businessName,
    branding: tenant.branding,
    role: membership.role,
    clientId: membership.clientId,
    status: 'active',
  });

  const path = membership.role === 'client' ? '/portal' : '/admin';
  const onTenantHost = useSession.getState().tenant?.tenantId === tenant.tenantId;
  return { path, url: onTenantHost ? null : membershipHomeUrl({ role: membership.role, slug: tenant.slug }) };
}
